import * as React from 'react';
import { observer } from 'mobx-react';
import { inject } from '../../utils';
import { withTheme } from 'styled-components';
import { styled } from '../../utils/styled';
import { propStyles } from '../../utils/propStyles';
import Icon from '../Icon';
import {
    DataStore,
    EntityStore,
    QueryStore,
    ScreenSizeStore,
    TranslationStore,
} from '../../stores';
import { computed } from 'mobx';
import { IQueryContext } from '@kurtosys/ksys-api-client/dist/models/commonTypes';
import getQueryResults from '../../utils/query/getQueryResults';
import DynamicLink from '../DynamicLink';
import { camelCase } from '@kurtosys/ksys-app-template/dist/utils/changeCase';
import {
    DownloadControlActions,
    LinkActionOptions,
} from '../../models/configuration/explorer/DownloadControls';
import { Codes, ErrorTracking } from '../../utils/errorHandling/ErrorTracking';
import { getOptionLabel } from '../../utils/document/downloadControls';
import { Theme } from '../../models/Theme';

const AdditionalElement = styled<any, any>(
    'div',
    'additionalElement',
    'downloadControlsAdditionalElement'
)`
    ${propStyles};
`;

AdditionalElement.defaultProps = {
    marginX: 'sm',
};

const AdditionalElementLabel = styled<any, any>(
    'span',
    'additionalElementLabel',
    'downloadControlsAdditionalElement'
)`
    ${propStyles};
`;

interface DownloadControlsAdditionalElementProps {
    actions: DownloadControlActions;
    additionalElement: LinkActionOptions;
    document: any;
    entityStore?: EntityStore;
    dataStore?: DataStore;
    queryStore?: QueryStore;
    screenSizeStore?: ScreenSizeStore;
    translationStore?: TranslationStore;
    theme?: Theme;
}

type StateType = 'active' | 'disabled';

@inject(
    'dataStore',
    'screenSizeStore',
    'translationStore',
    'queryStore',
    'entityStore'
)
@observer
class DownloadControlsAdditionalElement extends React.Component<
    DownloadControlsAdditionalElementProps
> {
    @computed
    get queryContext(): IQueryContext {
        const { dataStore, entityStore } = this.props;
        const { entityType } = dataStore.config;
        return {
            entityByType: {
                [entityType]: entityStore?.value,
            },
        };
    }

    @computed
    get actions(): DownloadControlActions {
        return this.props.actions;
    }

    @computed
    get url() {
        const { linkId, linkFilter } = this.props?.additionalElement;
        if (linkFilter) {
            return linkFilter;
        }
        if (linkId) {
            return linkId;
        }
        ErrorTracking.log('warn', {
            code: Codes.LINK_ACTION_REQUIRED_FIELDS,
            configStore: this.props.dataStore?.config,
            data: this.props.additionalElement,
        });
    }

    @computed
    get label(): string | undefined {
        const {
            document,
            additionalElement,
            entityStore,
            queryStore,
            screenSizeStore,
        } = this.props;
        const { labelOptions } = additionalElement;

        return getOptionLabel({
            document,
            entityStore,
            queryStore,
            screenSizeStore,
            labelOptions,
            labelKey: 'label',
            downloadControlActions: this.props.actions,
        });
    }

    @computed
    get ariaLabel(): string | undefined {
        const {
            document,
            additionalElement,
            entityStore,
            queryStore,
            screenSizeStore,
        } = this.props;
        const { labelOptions } = additionalElement;

        return getOptionLabel({
            document,
            entityStore,
            queryStore,
            screenSizeStore,
            labelOptions,
            labelKey: 'label',
            skipHiddenCheck: true,
            downloadControlActions: this.props.actions,
        });
    }

    @computed
    get disabledLabel(): string | undefined {
        const {
            document,
            additionalElement,
            entityStore,
            queryStore,
            screenSizeStore,
        } = this.props;
        const { labelOptions } = additionalElement;

        return getOptionLabel({
            document,
            entityStore,
            queryStore,
            screenSizeStore,
            labelOptions,
            labelKey: 'disabledLabel',
            downloadControlActions: this.props.actions,
        });
    }

    @computed
    get ariaDisabledLabel(): string | undefined {
        const {
            document,
            additionalElement,
            entityStore,
            queryStore,
            screenSizeStore,
        } = this.props;
        const { labelOptions } = additionalElement;

        return getOptionLabel({
            document,
            entityStore,
            queryStore,
            screenSizeStore,
            labelOptions,
            labelKey: 'disabledLabel',
            skipHiddenCheck: true,
            downloadControlActions: this.props.actions,
        });
    }

    @computed
    get isIconHidden(): boolean {
        return this.props?.additionalElement?.iconOptions?.hide;
    }

    @computed
    get activeIconFilename() {
        return this.props?.additionalElement?.iconOptions?.filename;
    }

    @computed
    get disabledIconFilename() {
        return this.props?.additionalElement?.iconOptions?.disabledFilename;
    }

    getIconComponent(stateType: StateType) {
        const { theme } = this.props;
        let filename;
        let iconLabel;
        let iconTheme;
        if (stateType === 'active') {
            filename = this.activeIconFilename;
            iconLabel = this.ariaLabel;
            iconTheme = theme.DownloadAddElementIcon;
        } else {
            filename = this.disabledIconFilename;
            iconLabel = this.ariaDisabledLabel;
            iconTheme = theme.DownloadAddElementIconDis;
        }
        if (!filename) {
            return null;
        }
        if (this.isIconHidden) {
            return null;
        }
        return (
            <Icon
                fileName={filename}
                alt={iconLabel || ''}
                title={iconLabel || ''}
                height="1.6em"
                width="1.6em"
                {...iconTheme}
            />
        );
    }

    getAdditionalElementContent(stateType: StateType) {
        const { theme } = this.props;
        const label = stateType === 'active' ? this.label : this.disabledLabel;

        return (
            <>
                {label && (
                    <AdditionalElementLabel {...theme.DownloadAddElementLabel}>
                        {label}
                    </AdditionalElementLabel>
                )}
                {this.getIconComponent(stateType)}
            </>
        );
    }

    onClick = (link: string) => {
        const { dataStore } = this.props;
        dataStore.logDataLayer(
            'additional_action_link',
            { url: link },
            'click'
        );
    };

    @computed
    get wrapperComponent(): React.ReactElement {
        const { entityStore, theme } = this.props;
        const entityClientCode = entityStore?.value?.clientCode;
        let metricContext = 'downloadControlAdditionalElement';
        if (entityClientCode) {
            metricContext = `${metricContext}-${camelCase(entityClientCode)}`;
        }
        if (this.label) {
            metricContext = `${metricContext}-${camelCase(this.label)}`;
        }
        const styleProps = {
            display: 'flex',
            alignItems: 'center',
        };

        if (this.url) {
            return (
                <DynamicLink
                    queryContext={this.queryContext}
                    config={{
                        url: this.url,
                        key: this.props.additionalElement?._key,
                    }}
                    metricContext={metricContext}
                    ariaLabelKey={'downloadControlsAdditionalElement'}
                    {...styleProps}
                    {...theme.DownloadAddElementLink}
                    disabledChildren={this.getAdditionalElementContent(
                        'disabled'
                    )}
                    onClick={this.onClick}
                />
            );
        }

        return (
            <AdditionalElement {...styleProps} {...theme.DownloadAddElement} />
        );
    }

    @computed
    get showActiveElement(): boolean {
        return (
            this.label !== undefined || this.activeIconFilename !== undefined
        );
    }

    additionalElementsQueryResults(query) {
        const { dataStore } = this.props;
        const { culture } = dataStore;

        return getQueryResults(
            this.queryContext,
            query,
            culture,
            dataStore.appDataProps
        );
    }

    render() {
        const AdditionalElementWrapper = this.wrapperComponent.type;
        return this.showActiveElement ? (
            <AdditionalElementWrapper {...this.wrapperComponent.props}>
                {this.getAdditionalElementContent('active')}
            </AdditionalElementWrapper>
        ) : null;
    }
}

export default withTheme(DownloadControlsAdditionalElement);



// WEBPACK FOOTER //
// ./src/Components/Document/DownloadControlsAdditonalElement.tsx